'use client'

import { Suspense, useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react'
import { useAuth } from '@/shared/hooks/useAuth'
import { ANALYTICS_ENABLED } from '@/shared/lib/analytics'
import { APP_REGISTRY } from '@/registry/apps'
import {
  resolveAvailableModuleIds,
  writeModuleFlagsCookie,
} from '@/shared/lib/moduleFlags'
import { useModuleFlagsStore } from '@/shared/stores/moduleFlagsStore'

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com'

// Initialize once, at module load, on the client only.
if (typeof window !== 'undefined' && ANALYTICS_ENABLED && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // We capture pageviews manually below — the App Router doesn't fire a full
    // page load on client-side navigations, so autocapture alone misses them.
    capture_pageview: false,
    capture_pageleave: true,
    // Session replay. Inputs are masked by default; emails/passwords stay safe.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
    },
  })
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Analytics off (no key, or non-production) → render children untouched.
  if (!ANALYTICS_ENABLED) return <>{children}</>

  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      <PostHogIdentifier />
      <ModuleFlagsRefresher />
      {children}
    </PHProvider>
  )
}

// Mirror the `module-<id>` feature flags into the module-availability store +
// cookie whenever PostHog (re)loads them — on boot, and again after identify()
// re-evaluates the flags for the signed-in user. Middleware and the server
// layout read the cookie, so flag changes take effect on the next
// load/navigation without any code deploy (see moduleFlags.ts).
function ModuleFlagsRefresher() {
  const posthog = usePostHog()
  const setAvailableIds = useModuleFlagsStore((s) => s.setAvailableIds)

  useEffect(() => {
    if (!posthog) return
    return posthog.onFeatureFlags(() => {
      const availableIds = resolveAvailableModuleIds(
        APP_REGISTRY.map((app) => app.id),
        (key) => posthog.getFeatureFlag(key)
      )
      setAvailableIds(availableIds)
      writeModuleFlagsCookie(availableIds)
    })
  }, [posthog, setAvailableIds])

  return null
}

// Manual pageview capture on every route change (path or query string).
function PostHogPageView() {
  const posthog = usePostHog()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!pathname) return
    let url = window.origin + pathname
    const search = searchParams?.toString()
    if (search) url += `?${search}`
    posthog?.capture('$pageview', { $current_url: url })
  }, [pathname, searchParams, posthog])

  return null
}

// Tie events to the signed-in Supabase user; reset on sign-out.
function PostHogIdentifier() {
  const posthog = usePostHog()
  const { user, loading } = useAuth()
  // Track whether we've identified, so we only reset on an actual sign-out —
  // calling reset() while already anonymous churns the distinct_id every load.
  const identified = useRef(false)

  useEffect(() => {
    if (loading) return
    if (user) {
      posthog?.identify(user.id, { email: user.email })
      identified.current = true
    } else if (identified.current) {
      posthog?.reset()
      identified.current = false
    }
  }, [user, loading, posthog])

  return null
}
